import { NavigateLeft, NavigateRight } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import classnames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../../Button/Button';

import styles from './SegmentedControl.module.scss';
import { Props } from './SegmentedControl.types';

/**
 * @deprecated use `ToggleMenu` instead
 * @example import { ToggleMenu, ToggleMenuItem } from '@randstad-lean-mobile-factory/react-components-core';
 *
 *          <ToggleMenu value={value} onChange={setValue}>
 *            <ToggleMenuItem value="0" disabled>rubrique name</ToggleMenuItem>
 *            <ToggleMenuItem value="1">rubrique name</ToggleMenuItem>
 *          </ToggleMenu>
 */
export const SegmentedControl = <T,>({
  selected,
  controls,
  onSelectionChange,
  getValue,
  className,
  labelClassName,
  selectedBackgroundClassName,
}: Props<T>) => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const childRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectedBackgroundRef = useRef<HTMLDivElement>(null);
  const [contained, setContained] = useState<boolean>(false);

  const getDistanceToBase = useCallback((index: number): number => {
    return childRefs.current.reduce((previousValue, childRef, currentIndex) => {
      if (childRef && currentIndex < index) {
        return previousValue + childRef.getBoundingClientRect().width;
      }
      return previousValue;
    }, 0);
  }, []);

  const handleMovement = useCallback(() => {
    if (mainContainerRef.current) {
      childRefs.current.forEach((childRef, index) => {
        if (childRef) {
          if (selectedBackgroundRef.current && mainContainerRef.current) {
            if (index === controls.indexOf(selected)) {
              const distance = getDistanceToBase(index);
              const childWidth = childRef.getBoundingClientRect().width;
              selectedBackgroundRef.current.style.width = `${childWidth}px`;
              selectedBackgroundRef.current.style.transform = `translateX(${distance}px)`;
              if (contained) {
                mainContainerRef.current.scrollTo({
                  behavior: 'smooth',
                  left: Math.max(
                    distance -
                      mainContainerRef.current.getBoundingClientRect().width / 2 +
                      childWidth / 2,
                    0
                  ),
                });
              }
            }
          }
        }
      });
      const totalChildrenWidth = childRefs.current.reduce((accumulator, childRef) => {
        if (childRef) {
          return accumulator + childRef.getBoundingClientRect().width;
        }
        return accumulator;
      }, 0);
      if (totalChildrenWidth - 1 > mainContainerRef.current.getBoundingClientRect().width) {
        setContained(true);
      } else {
        setContained(false);
      }
    }
  }, [controls, getDistanceToBase, selected, contained]);

  useEffect(() => {
    handleMovement();
  }, [controls, getDistanceToBase, handleMovement, selected]);

  useEffect(() => {
    setTimeout(handleMovement);
  }, [contained, handleMovement]);

  useEffect(() => {
    window.addEventListener('resize', handleMovement);
    return () => window.removeEventListener('resize', handleMovement);
  }, [handleMovement]);

  return (
    <div className={styles.mainContainer}>
      {contained && (
        <Button
          size="medium"
          onClick={() => {
            const index = controls.indexOf(selected);
            onSelectionChange(controls[(index + controls.length - 1) % controls.length]);
          }}
        >
          <NavigateLeft />
        </Button>
      )}
      <div className={classnames(styles.container, className)} ref={mainContainerRef}>
        {controls.map((control, index) => (
          <div
            key={`${control}`}
            className={classnames(styles.control, labelClassName, {
              [styles.selected]: selected === control,
            })}
            ref={el => {
              childRefs.current[index] = el;
            }}
            onClick={() => {
              onSelectionChange(control);
            }}
          >
            <span className={styles.text}>{getValue(control)}</span>
          </div>
        ))}
        <div
          ref={selectedBackgroundRef}
          className={classnames(styles.selectedBackground, selectedBackgroundClassName)}
        >
          <div />
        </div>
      </div>
      {contained && (
        <Button
          size="medium"
          onClick={() => {
            const index = controls.indexOf(selected);
            onSelectionChange(controls[(index + 1) % controls.length]);
          }}
        >
          <NavigateRight />
        </Button>
      )}
    </div>
  );
};
